import React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/overlays';
import { Button, Field, Input } from '@/components/ui/primitives';
import { useAuth } from '@/lib/auth';
import { CONNECTION_COPY } from '@/lib/sessionGate';
import { ApiError } from '@/lib/api';

/**
 * Sign in again, without leaving the page.
 *
 * This exists for one scenario: a VA four minutes into typing call notes when
 * their seven-day session runs out. The previous behaviour cleared the cached
 * user, which unmounted the route and took the notes with it. Nothing about
 * that was recoverable — the notes had never been sent anywhere.
 *
 * So this is deliberately a layer, not a page. The route underneath keeps its
 * React state; the person types their password, the dialog closes, and they
 * press Save again on the form that is still sitting there.
 *
 * Two things it does NOT do:
 *
 *   - It does not replay the request that failed. There is no mutation queue in
 *     LeadForge, and pretending otherwise would silently re-submit a call
 *     outcome the person may have changed their mind about. They retry, once,
 *     when they can see the result.
 *   - It does not let someone sign in as a different account in place. The
 *     screen behind the dialog was rendered for the previous user and may hold
 *     leads the next one is not assigned. Switching accounts goes through the
 *     normal login page, which is a deliberate choice with a visible cost.
 *
 * This is not a second authentication system: it calls the same `login` on the
 * same context as the login page.
 */
export function SessionExpiredDialog(): JSX.Element {
  const { login, logout, user } = useAuth();
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const username = user?.username ?? '';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username, password);
      setPassword('');
      toast.success('Signed back in. Try that action again.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    // Always open, and closable only by signing in or leaving deliberately.
    // A dismissible dialog would leave someone clicking a dead application and
    // wondering why every save fails.
    <Dialog open>
      <DialogContent
        size="sm"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="border-b border-border px-4 py-3">
          <DialogTitle>{CONNECTION_COPY.sessionExpiredTitle}</DialogTitle>
          <DialogDescription className="mt-1">
            {CONNECTION_COPY.sessionExpiredBody}
          </DialogDescription>
        </div>
        <form onSubmit={submit}>
          <DialogBody className="space-y-3">
            <Field label="Signed in as">
              {/* Read-only: see the note above about switching accounts. */}
              <Input value={username} readOnly autoComplete="username" tabIndex={-1} />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </Field>
            {error && <p className="text-xs text-danger">{error}</p>}
          </DialogBody>
          <DialogFooter className="justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => void logout()}
              title="Leaves this page. Anything unsaved on it is lost."
            >
              Sign in as someone else
            </Button>
            <Button type="submit" disabled={busy || !password}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
